import CustomerService from "@/services/premium.service";
import { Module } from "vuex";
import {
  NumbersMapStruct,
  PremiumCategoryStruct,
  PremiumNumberStruct,
} from "@/struct/PremiumStruct";
import { i18n } from "@/i18n";

const premiumService = new CustomerService();

export interface PremiumState {
  categories: PremiumCategoryStruct[] | null;
  numbersMap: Map<string, NumbersMapStruct>;
  isLoadingListing: boolean;
}
interface PayloadPremiumPhone {
  search: string;
  limit: number;
  start: number;
  category: string;
}

const DEFAULT_LIMIT = 10;

const getIsFilledNumber = (numbers: NumbersMapStruct[]): boolean => {
  if (!numbers) return true;
  return numbers.length === 0 || numbers.length % DEFAULT_LIMIT !== 0;
};

const premiumModule: Module<PremiumState, any> = {
  namespaced: true,
  state: () => ({
    categories: null,
    numbersMap: new Map(),
    isLoadingListing: false,
  }),
  getters: {
    premiumTabs(state) {
      const baseTabs = [
        {
          title: i18n.global.t("premium.tabs.all"),
          name: "all",
        },
      ];
      const categories = state.categories;
      if (!Array.isArray(categories)) return baseTabs; // Возвращаем пустой массив, если данных еще нет
      return [
        ...baseTabs,
        ...categories.map((item: any) => ({
          title: item.displayName,
          name: item.name,
        })),
      ];
    },
  },
  mutations: {
    SET_CATEGORIES(state, value: PremiumCategoryStruct[]) {
      state.categories = value;
    },
    SET_CATEGORY_DATA(
      state,
      payload: { type: string; data: NumbersMapStruct }
    ) {
      state.numbersMap.set(payload.type, payload.data);
    },
    PUSH_CATEGORY_DATA(
      state,
      payload: {
        type: string;
        newNumbers: PremiumNumberStruct[];
        isFilled: boolean;
      }
    ) {
      const currentData = state.numbersMap.get(payload.type);

      if (currentData) {
        currentData.numbers.push(...payload.newNumbers);
        currentData.isFilled = payload.isFilled;
      } else {
        state.numbersMap.set(payload.type, {
          numbers: payload.newNumbers,
          isFilled: payload.isFilled,
        });
      }
    },
    SET_NUMBERS_MAP(state, newMap: Map<string, NumbersMapStruct>) {
      state.numbersMap = newMap;
    },
    SET_IS_LOADING(state, value: boolean) {
      state.isLoadingListing = value;
    },

    CLEAR_NUMBERS(state) {
      state.numbersMap.clear();
    },
  },
  actions: {
    changeNumberToPremium(
      { dispatch },
      { number, type }: { number: number; type: string }
    ) {
      premiumService
        .changePremiumNumber(number, type)
        .then((res) => {
          dispatch("auth/logout", null, { root: true });
          dispatch(
            "system/openModal",
            { presetKey: "premium_change_success" },
            { root: true }
          );
          return res;
        })
        .catch((err) => {
          if (err?.response?.data?.code === 66) {
            dispatch(
              "system/openModal",
              { presetKey: "premium_change_error_6months" },
              { root: true }
            );
          } else {
            dispatch(
              "system/openModal",
              { presetKey: "premium_change_error_taken" },
              { root: true }
            );
          }
        });
    },

    updatePremiumCategories({ commit }) {
      return premiumService
        .getPremiumCategory()
        .then((res) => {
          if (res.data?.status) {
            commit("SET_CATEGORIES", res.data.response);
            return true;
          }
          return false;
        })
        .catch((err) => {
          console.error("Ошибка при получении премиум категорий:", err);
          return false;
        });
    },

    updatePremiumNumbers(
      { commit, rootGetters, state },
      payload?: { search: string }
    ) {
      commit("SET_IS_LOADING", true);
      commit("CLEAR_NUMBERS");
      const facilityId =
        rootGetters["tariff/mainTariff"]?.facility?.toString() || "";

      if (!state.categories?.length) return Promise.resolve(false);

      const requests = state.categories.map((category) => {
        const params = {
          facilityId,
          type: category.name,
          search: payload?.search || "",
          limit: DEFAULT_LIMIT,
          start: 0,
        };

        return premiumService
          .getPremiumNumbers(params)
          .then((res) => ({
            type: category.name,
            numbers: res?.data?.response || [],
            isFilled: getIsFilledNumber(res?.data?.response),
          }))
          .catch((err) => {
            console.error(
              `Не удалось обновить категорию ${category.name}`,
              err
            );
            return {
              type: category.name,
              numbers: [],
              isFilled: true,
            };
          });
      });
      return Promise.all(requests)
        .then((results) => {
          const freshMap = new Map<string, NumbersMapStruct>();

          results.forEach((item) => {
            freshMap.set(item.type, {
              numbers: item.numbers,
              isFilled: getIsFilledNumber(item.numbers),
            });
          });

          commit("SET_NUMBERS_MAP", freshMap);
          return true;
        })
        .catch((err) => {
          console.error("Ошибка при общем обновлении премиум-номеров", err);
          return false;
        })
        .finally(() => {
          commit("SET_IS_LOADING", false);
        });
    },
    fetchNumbersByCategory(
      { commit, rootGetters, state },
      payload: PayloadPremiumPhone
    ) {
      commit("SET_IS_LOADING", true);
      const facilityId =
        rootGetters["tariff/mainTariff"]?.facility?.toString() || "";

      const params = {
        facilityId,
        type: payload.category,
        search: payload.search,
        start: payload.start,
        limit: DEFAULT_LIMIT,
      };

      return premiumService
        .getPremiumNumbers(params)
        .then((res) => {
          const freshNumbers = res?.data?.response || [];

          if (payload.start === 0) {
            commit("SET_CATEGORY_DATA", {
              type: payload.category,
              data: {
                numbers: freshNumbers,
                isFilled: getIsFilledNumber(freshNumbers),
              },
            });
          } else {
            commit("PUSH_CATEGORY_DATA", {
              type: payload.category,
              newNumbers: freshNumbers,
              isFilled: getIsFilledNumber(freshNumbers),
            });
          }

          return true;
        })
        .catch((err) => {
          console.error(
            `Не удалось обновить категорию ${payload.category}`,
            err
          );
          return false;
        })
        .finally(() => {
          commit("SET_IS_LOADING", false);
        });
    },
  },
};

export default premiumModule;
